/**
 * Replace / insert panel (§6.5.4): given the currently selected timeline item's
 * purpose + character, fetch candidate segments from the same project. Click a
 * candidate → swap into the timeline. Includes a search input for free-text
 * filtering and a hand-roll filter by purpose.
 */
import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ChevronRight, Loader2, RefreshCcw, Search } from 'lucide-react'

import { dramaApi } from '@/lib/api/drama'
import type { DramaLang, DramaSegment, TimelineItem, TimelinePurpose } from '@/types/drama'
import { dramaT } from './i18n'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { SegmentCard, PURPOSE_STYLE } from './SegmentCard'

interface Props {
  projectId: string
  currentItem?: TimelineItem
  excludeSegmentCodes: string[]
  allSegments: DramaSegment[]
  lang: DramaLang
  onReplace: (segmentCode: string) => void
}

const PURPOSE_OPTIONS: TimelinePurpose[] = Object.keys(PURPOSE_STYLE) as TimelinePurpose[]

export function SegmentReplacePanel({
  projectId,
  currentItem,
  excludeSegmentCodes,
  allSegments,
  lang,
  onReplace,
}: Props) {
  const t = dramaT(lang)
  // Initial candidate list = all segments not currently used, sorted by hook desc
  const [candidates, setCandidates] = useState<DramaSegment[]>(() =>
    [...allSegments]
      .filter((s) => !excludeSegmentCodes.includes(s.segment_code))
      .sort((a, b) => (b.hook_score ?? 0) - (a.hook_score ?? 0))
      .slice(0, 30),
  )
  const [purposeFilter, setPurposeFilter] = useState<TimelinePurpose | ''>('')
  const [textQuery, setTextQuery] = useState('')
  // [REFACTOR-2026-06-08] Candidate list collapsed by default to keep the
  // inspector compact; expand to browse / replace.
  const [showCandidates, setShowCandidates] = useState(false)

  const searchMutation = useMutation({
    mutationFn: () =>
      dramaApi.searchSegments(projectId, {
        plot_tags_any: purposeFilter ? mapPurposeToPlotTags(purposeFilter) : undefined,
        text_query: textQuery || undefined,
        exclude_segment_codes: excludeSegmentCodes,
        limit: 30,
      }),
    onSuccess: (data) => setCandidates(data),
  })

  const segmentPurpose = currentItem?.purpose

  const filtered = useMemo(() => {
    if (!purposeFilter && !textQuery) return candidates
    let arr = candidates
    if (textQuery) {
      const q = textQuery.toLowerCase()
      arr = arr.filter(
        (s) =>
          (s.summary || '').toLowerCase().includes(q) ||
          (s.transcript || '').toLowerCase().includes(q),
      )
    }
    return arr
  }, [candidates, purposeFilter, textQuery])

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('srp.title')}</h3>
        {segmentPurpose && (
          <span className="text-xs text-muted-foreground">
            {t('srp.currentPurpose')}<b>{segmentPurpose}</b>
          </span>
        )}
      </div>

      <div className="space-y-2">
        <select
          className="w-full px-2 py-1.5 text-sm border rounded-md bg-background"
          value={purposeFilter}
          onChange={(e) => setPurposeFilter(e.target.value as TimelinePurpose | '')}
        >
          <option value="">{t('srp.allPurposes')}</option>
          {PURPOSE_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {t(`purpose.${p}` as any)}
            </option>
          ))}
        </select>

        <div className="flex gap-2">
          <Input
            value={textQuery}
            onChange={(e) => setTextQuery(e.target.value)}
            placeholder={t('srp.searchPlaceholder')}
            className="flex-1"
          />
          <Button
            size="sm"
            onClick={() => searchMutation.mutate()}
            disabled={searchMutation.isPending}
          >
            {searchMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Search className="w-3.5 h-3.5" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setTextQuery('')
              setPurposeFilter('')
              setCandidates(
                [...allSegments]
                  .filter((s) => !excludeSegmentCodes.includes(s.segment_code))
                  .sort((a, b) => (b.hook_score ?? 0) - (a.hook_score ?? 0))
                  .slice(0, 30),
              )
            }}
          >
            <RefreshCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowCandidates((v) => !v)}
        className="flex items-center gap-1 w-full text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showCandidates ? 'rotate-90' : ''}`} />
        {t('srp.candidatesToggle', filtered.length)}
      </button>

      {showCandidates && (
      <div className="space-y-2 max-h-[480px] overflow-y-auto">
        {filtered.map((s) => (
          <button
            key={s.segment_code}
            onClick={() => onReplace(s.segment_code)}
            className="w-full text-left p-2 rounded-md border hover:bg-accent/40 transition-colors flex items-start gap-2"
          >
            <SegmentCard
              segmentCode={s.segment_code}
              episodeNumber={s.episode_number}
              purpose={(segmentPurpose ?? 'plot_progression') as TimelinePurpose}
              durationLabel={`${(s.end_seconds - s.start_seconds).toFixed(1)}s`}
            />
            <div className="flex-1 min-w-0 text-xs">
              <div className="line-clamp-2 mb-1">
                {Boolean(s.metadata?.full_episode) && (
                  <span className="inline-block mr-1 px-1 rounded bg-violet-500/15 text-violet-600 dark:text-violet-300 text-[10px] font-medium">
                    {t('srp.fullEpisode')}
                  </span>
                )}
                {s.summary || t('srp.noSummary')}
              </div>
              <div className="text-muted-foreground line-clamp-1 italic">
                {s.transcript?.slice(0, 80) || '—'}
              </div>
              <div className="text-muted-foreground mt-1">
                {s.hook_score != null && <span>🪝 {s.hook_score.toFixed(1)} </span>}
                {s.conflict_score != null && <span>⚡ {s.conflict_score.toFixed(1)}</span>}
              </div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            {t('srp.noMatch')}
          </div>
        )}
      </div>
      )}
    </div>
  )
}

// [SOP-2026-06-04] Loose mapping helper: route SOP purposes to the real plot
// tags produced by segment_tagging for quick candidate filtering (§6.4).
function mapPurposeToPlotTags(purpose: TimelinePurpose): string[] {
  switch (purpose) {
    case 'opening_highlight':
    case 'final_highlight':
      return ['revenge', 'reveal', 'public_conflict']
    case 'emotional_peak':
      return ['confession', 'tension', 'public_conflict']
    case 'plot_progression':
      return ['tension']
    case 'reverse_context':
      return ['family']
    case 'voiceover_setup':
    case 'tail_card':
    default:
      return []
  }
}
